import { ref, computed, onMounted } from 'vue';
import dayjs from 'dayjs';
import { ErrorUpdate } from '@/components/Molecules/Field/ConnxField.vue';
import { createDateSchema } from '@/validation/date';

const DEFAULT_MONTHS = 6;

export function useDateRangeSettings(props: any, emit?: any) {
  /**
   * ===============================
   * Local state
   * ===============================
   */

  // Track the last valid value of previousMonths
  const lastValidPreviousMonths = ref<number | null>(null);

  const hadInitialPreviousRangeValue = ref<boolean>(false);

  onMounted(() => {
    // Check if previousRange had a value when composable was created
    hadInitialPreviousRangeValue.value = !!props?.previousRange?.value?.[0];
  });

  /**
   * ===============================
   * Two-way bound fields (computed setters)
   * ===============================
   */

  const handleFieldError = (error: ErrorUpdate) => {
    emit('field-error', error);
  };

  const previousMonths = computed<number | null>({
    get() {
      const value = props?.previousMonths?.value ?? null;
      // Update last valid value when we have a valid number
      if (value !== null && value !== undefined && typeof value === 'number') {
        lastValidPreviousMonths.value = value;
      }
      return value;
    },
    set(value) {
      emit?.('update:previous-months', value);
      // Update last valid value when setting a valid number
      if (value !== null && value !== undefined && typeof value === 'number') {
        lastValidPreviousMonths.value = value;
      }
    },
  });

  const clearErrors = () => {
    ['previousMonths', 'previousRangeFrom', 'previousRangeTo'].forEach((name) => {
      emit('field-error', { name, error: '' });
    });
  };

  const withinXMonths = computed<number | null>({
    get() {
      return props?.withinXMonths?.value ?? null;
    },
    set(value) {
      emit?.('update:within-x-months', value);
    },
  });

  const reportingPeriodChoice = computed<'previousMonths' | 'previousRange'>({
    get() {
      return props?.previousRange?.selected ? 'previousRange' : 'previousMonths';
    },
    set(value) {
      if (value === 'previousMonths' && (!previousMonths.value || previousMonths.value === null)) {
        // Use last valid value if available, otherwise fallback to default (6)
        const defaultValue = lastValidPreviousMonths.value || DEFAULT_MONTHS;
        previousMonths.value = defaultValue;
      }
      if (value === 'previousRange') {
        // Check if props?.previousRange?.value?.[0] was set first
        if (!hadInitialPreviousRangeValue.value) {
          // If no value, use previousMonths.value to calculate months back from today
          const monthsToGoBack = props?.previousMonths?.value || DEFAULT_MONTHS;
          const calculatedDate = dayjs().subtract(monthsToGoBack, 'month');
          props.previousRange.value[0] = calculatedDate.format('YYYY-MM-DD');
        }
      }
      clearErrors();
      emit?.('update:previous-range-selected', value === 'previousRange');
    },
  });

  // Rest of your code remains the same...
  const previousRangeFrom = computed<Date | null>({
    get() {
      const value = props?.previousRange?.value?.[0];
      if (!value || value.trim() === '') return null;
      return dayjs(value).toDate();
    },
    set(date) {
      if (!emit) return;

      if (!date) {
        emit('update:previous-range-from', '');
        return;
      }

      const formatted =
        props?.previousRange?.type === 'MY'
          ? dayjs(date).startOf('month').format('YYYY-MM-DD')
          : dayjs(date).format('YYYY-MM-DD');

      emit('update:previous-range-from', formatted);
    },
  });

  const previousRangeTo = computed<Date | null>({
    get() {
      const value = props?.previousRange?.value?.[1];
      if (!value || value.trim() === '') return null;
      return dayjs(value).toDate();
    },
    set(date) {
      if (!emit) return;

      if (!date) {
        emit('update:previous-range-to', '');
        return;
      }

      const formatted =
        props?.previousRange?.type === 'MY'
          ? dayjs(date).endOf('month').format('YYYY-MM-DD')
          : dayjs(date).format('YYYY-MM-DD');

      emit('update:previous-range-to', formatted);
    },
  });

  /**
   * ===============================
   * Local-only state
   * ===============================
   */

  const previousRangeFromTouched = ref(false);

  /**
   * ===============================
   * Derived values (math only)
   * ===============================
   */

  const withinXMonthsMax = computed<number | undefined>(() => {
    if (reportingPeriodChoice.value === 'previousMonths') {
      return props?.previousMonths?.value ?? null;
    }

    if (reportingPeriodChoice.value === 'previousRange' && previousRangeFrom.value && previousRangeTo.value) {
      const from = dayjs(previousRangeFrom.value);
      const to = dayjs(previousRangeTo.value);
      return Math.ceil(to.diff(from, 'month', true));
    }

    return null;
  });

  const previousRangeMin = computed<Date | undefined>(() => {
    return previousRangeFrom.value ?? undefined;
  });

  const previousRangeMax = computed<Date | undefined>(() => {
    return previousRangeTo.value ?? undefined;
  });

  /**
   * ===============================
   * Validation rules
   * ===============================
   */

  const fromDateRules = createDateSchema({
    required: true,
    requiredMessage: 'From date is required',
    compareTo: previousRangeTo,
    comparison: 'before',
    comparisonMessage: 'From must be before To',
  });

  const toDateRules = createDateSchema({
    required: false,
    compareTo: previousRangeFrom,
    comparison: 'after',
    comparisonMessage: 'To must be after From',
  });

  /**
   * ===============================
   * Exposed API
   * ===============================
   */

  return {
    previousMonths,
    withinXMonths,
    withinXMonthsMax,
    previousRangeFrom,
    previousRangeTo,
    previousRangeMin,
    previousRangeMax,
    previousRangeFromTouched,
    reportingPeriodChoice,
    fromDateRules,
    toDateRules,
    handleFieldError,
  };
}
